import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { AbstractLayoutCommand } from './AbstractLayoutCommand';
import { AddLayoutElementsCommand } from './AddLayoutElementsCommand';
import { ClearLayoutElementsCommand } from './ClearLayoutElementsCommand';

import type { VideoElement } from '../models';
import type { Element } from '../models/Element';
import type { NaturalDimensions, VideoDimensions } from '../types/aspectRatio';
import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

export interface SwitchLayoutElementParameters {
  layoutType: 'stacked-1-1' | 'stacked-1-3';
  videoSourceDimensionsPrev?: VideoDimensions;
  videoSourceDimensionsCurr?: VideoDimensions;
  naturalMediaDimensions?: Record<VideoElement['source'], NaturalDimensions>;
}

export interface ResultElements {
  top: Element;
  bottom: Element;
  parent: Element;
}

export class SwitchLayoutTypeCommand extends AbstractLayoutCommand {
  private params: SwitchLayoutElementParameters;

  getName(): string {
    return 'SwitchLayoutTypeCommand';
  }
  private _resultElements: ResultElements | null = null;

  constructor(
    editor: VideoEditor,
    element: Element,
    params: SwitchLayoutElementParameters,
  ) {
    super(editor, element);
    this.params = params;
  }

  execute(): boolean {
    if (!this.sourceElement) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'No source element provided',
      });
      return false;
    }

    super.execute();

    const {
      videoSourceDimensionsPrev,
      videoSourceDimensionsCurr,
      naturalMediaDimensions,
      layoutType,
    } = this.params;

    let command: Command = new ClearLayoutElementsCommand(
      this.editor,
      this.sourceElement,
      {
        fit: 'cover',
        videoSourceDimensionsPrev,
        videoSourceDimensionsCurr,
        naturalMediaDimensions,
      },
    );
    let success = this.editor.executeCommand(command);
    if (!success) {
      return false;
    }

    command = new AddLayoutElementsCommand(this.editor, this.sourceElement, {
      layoutType,
    });
    success = this.editor.executeCommand(command);
    if (!success) {
      return false;
    }

    const resultElements = (
      command as AddLayoutElementsCommand
    ).getResultElements();
    if (!resultElements) {
      return false;
    }

    this._resultElements = {
      top: resultElements[0],
      bottom: resultElements[1],
      parent: this.sourceElement,
    };
    this.canUndo = true;

    return true;
  }

  getDescription(): string {
    return `switching layout type`;
  }

  getResultElements(): ResultElements | null {
    if (!this._resultElements) {
      return null;
    }
    return this._resultElements;
  }
}
